import type { SceneJsonDto } from '@seddi/scene';

import { getApiAddress, getApiSkipAuth, getApiFullStyleEndpoint } from './apiConfig';
import type { MockupLogo } from '../types/mockupLogos';
import { isMobile } from '../utils/utils';

export type CatalogStyle = {
  id: string;
  brand: string;
  name: string;
  [key: string]: unknown;
};

export enum StyleSceneQuality {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export type Style = {
  id: string;
  brand: string;
  name: string;
  color?: string;
  [key: string]: unknown;
};

export type StyleLogoRegion = {
  id: string;
  name: string;
  pieceName: string;
  side: string;
  size: { x: number; y: number };
  transform: unknown;
  isBrandLogoRegion?: boolean;
  entityId?: string;
  [key: string]: unknown;
};

export type PointOfMeasure = {
  id: string;
  name: string;
  code?: string;
  [key: string]: unknown;
};

export type StyleSize = {
  id: string;
  label: string;
  code?: string;
  [key: string]: unknown;
};

export type StyleColorwaySummary = {
  styleId: string;
  color: string;
  [key: string]: unknown;
};

export type Mockup = {
  id: string;
  name: string;
  preview?: string;
  useBrandLogos?: boolean;
  liveblocksData?: {
    editorSceneLogos?: {
      hideTearAwayLabel?: boolean;
      logos?: MockupLogo[];
    };
  };
  [key: string]: unknown;
};

export type StyleAggregatorParams = {
  apiKey: string;
  brand?: string;
  styleNumber?: string;
  colorway?: string;
  mockupId?: string;
  signal?: AbortSignal;
};

export type StyleAggregatorData = {
  brand: string;
  styleNumber: string;
  colorway?: string;
  styleId: string;
  style: Style;
  catalogStyle?: CatalogStyle;
  scene: SceneJsonDto;
  styleRegions: StyleLogoRegion[];
  pointsOfMeasure: PointOfMeasure[];
  sizes: StyleSize[];
  availableColorways?: StyleColorwaySummary[];
  mockup?: Mockup;
};

export class StyleNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StyleNotFoundError';
  }
}

const FULL_STYLE_ENDPOINT = getApiFullStyleEndpoint();
const QUALITY = isMobile() ? StyleSceneQuality.LOW : StyleSceneQuality.MEDIUM;

function buildHeaders(apiKey: string): HeadersInit {
  return getApiSkipAuth() ? {} : { 'content-type': 'application/json', 'x-api-key': apiKey };
}

async function fetchFullStyleData(params: StyleAggregatorParams): Promise<StyleAggregatorData> {
  const { apiKey, signal } = params;

  const search = new URLSearchParams({
    quality: QUALITY,
  });

  if (params.mockupId) {
    search.set('mockup_id', params.mockupId);
  } else {
    const { brand, styleNumber, colorway } = params;

    if (!brand || !styleNumber) {
      throw new Error('Missing required parameters "brand" and/or "styleNumber" for style lookup.');
    }

    search.set('brand', brand);
    search.set('number', styleNumber);
    if (colorway) search.set('colorway', colorway);
  }

  const url = `${getApiAddress()}/${FULL_STYLE_ENDPOINT}?${search.toString()}`;
  const response = await fetch(url, {
    headers: buildHeaders(apiKey),
    signal,
  });
  if (!response.ok) {
    if (response.status === 404) {
      if (params.mockupId) {
        throw new StyleNotFoundError(`No styles found for Mockup ${params.mockupId}`);
      }

      const { brand, styleNumber, colorway } = params;
      throw new StyleNotFoundError(
        `No styles found for Brand ${brand}, Style Number ${styleNumber}, Colorway ${colorway ?? 'ANY'}`,
      );
    }
    throw new Error(`Failed to fetch full style data: ${response.status}`);
  }
  return response.json() as Promise<StyleAggregatorData>;
}

export const fetchStyleAggregatorData = async (
  params: StyleAggregatorParams,
): Promise<StyleAggregatorData> => {
  return fetchFullStyleData(params);
};
